import { apiClient } from '$lib/api/client';

/**
 * What one industry category is made of, as opposed to which companies are in
 * it.
 *
 * # It answers a question the picker cannot
 *
 * `/deep-iq/industry-centroids` gives a name and a number per category, which
 * is right for a list of 250 rows and is the whole of what a customer choosing
 * one currently sees. This is the other reading of the same population: how
 * big the companies are, where they are, how many we can actually reach, and
 * what the register has published about them lately.
 *
 * Nothing here names a company or a person — it is the shape of a population,
 * not its members — which is why the endpoint is unguarded and why this module
 * needs no Turnstile token.
 *
 * # There is no denominator on the size bands, deliberately
 *
 * The bands cover the category. A headcount is derived from the register for
 * every company, with an observed figure taking precedence where one exists,
 * so there is no measured minority to report a coverage figure for. `capital`
 * is the one panel that carries a denominator, and it is not a data gap: a
 * sole proprietorship has no nominal capital to register, so `basis` is the
 * members in a form that can hold one. Printing `known` against
 * `company_count` reports company law as missing data.
 */

/** One headcount band and how many of the category sit in it. */
export interface BandCount {
  band: string;
  count: number;
}

export interface SizeSpread {
  bands: BandCount[];
}

/**
 * Registered capital, in francs.
 *
 * `basis` is the members in a capital-bearing legal form; `known` is how many
 * of those carry a figure. The gap between the two is real missing data. The
 * gap between `basis` and the category's size is company law.
 */
export interface CapitalSpread {
  basis: number;
  known: number;
  median: number;
  quartile_1: number;
  quartile_3: number;
}

/**
 * One legal form and how many of the category take it.
 *
 * `key` names the four forms the service knows by name and is absent for the
 * other seven the register numbers.
 */
export interface LegalFormCount {
  form: number;
  key?: string;
  count: number;
}

/**
 * The register's codes for the four forms this product names.
 *
 * The same numbers as internal/domain/companyrecord.go, which is where they are
 * defined. They are here rather than inline at the one place that groups them
 * because a bare 3 and 4 in a filter reads as nothing at all.
 */
export const LEGAL_FORM = {
  soleProprietorship: 1,
  partnership: 2,
  corporation: 3,
  limitedLiability: 4
} as const;

/** How many companies were founded in one completed year. */
export interface YearCount {
  year: number;
  count: number;
}

/** How many were founded in one month of the running year. */
export interface MonthCount {
  year: number;
  month: number;
  count: number;
}

/**
 * A canton or a city and its population.
 *
 * `lat` and `lon` are present where the place resolved, so a map needs no
 * geocoding service. They are absent for one that did not.
 */
export interface PlaceCount {
  name: string;
  count: number;
  lat?: number;
  lon?: number;
}

/** How many of the category we hold a way of contacting. */
export interface ReachSpread {
  website: number;
  email: number;
  phone: number;
  linkedin: number;

  /**
   * How many can be reached by post: a postcode and a town, plus a street or a
   * post-office box.
   *
   * Nearly all of them — 98.8% across the register — but counted rather than
   * assumed. A company in liquidation can have its domicile revoked, so a
   * category full of winding-up companies really is less reachable by post.
   */
  postal: number;
}

/**
 * One window's register events, by kind.
 *
 * `executives` counts changes to who runs these companies and never names one:
 * those rows are about natural persons and this endpoint is readable by
 * anybody.
 */
export interface SignalCounts {
  capital: number;
  location: number;
  name: number;
  executives: number;
  new_companies: number;
}

/**
 * The three windows the service reports.
 *
 * Three rather than one because the reading worth having is the comparison:
 * thirty days against a twelfth of the year says whether something is
 * happening now or is merely the usual rate.
 */
export interface SignalSpread {
  days_30: SignalCounts;
  days_90: SignalCounts;
  days_365: SignalCounts;
}

/**
 * The profile of one category.
 *
 * Each of `legal_forms_other`, `cantons_other` and `cities_other` is what its
 * distribution does not account for, so list plus remainder equals
 * `company_count` in every case. `cities_other` is the large one by
 * construction — `cities` is the twenty biggest towns and most of a category
 * lives outside them — and a screen that hides it is implying the list is the
 * whole category.
 *
 * `as_of` is the date of the newest register event held, not the time of the
 * request. A screen rendered from this should date itself by that: an import
 * that failed on Friday should say Thursday, not today.
 */
export interface IndustryDetails {
  id: number;
  name: string;
  industry_id?: number;
  industry_name?: string;
  description?: string;

  company_count: number;
  contact_count: number;

  sizes: SizeSpread;
  capital: CapitalSpread;
  founding: YearCount[];
  founding_months: MonthCount[];
  legal_forms: LegalFormCount[];
  cantons: PlaceCount[];
  cities: PlaceCount[];

  legal_forms_other: number;
  cantons_other: number;
  cities_other: number;

  reach: ReachSpread;
  signals: SignalSpread;
  as_of?: string;
}

const noSignals: SignalCounts = {
  capital: 0,
  location: 0,
  name: 0,
  executives: 0,
  new_companies: 0
};

const noReach: ReachSpread = { website: 0, email: 0, phone: 0, linkedin: 0, postal: 0 };

const noCapital: CapitalSpread = { basis: 0, known: 0, median: 0, quartile_1: 0, quartile_3: 0 };

/**
 * Ask what one category is made of.
 *
 * The response is normalised at this boundary rather than at each use. A
 * deployment older than a field sends no field, and `undefined.length` throws
 * where a missing list would merely have drawn nothing — which does not fail
 * politely: the whole panel stops rendering, so the customer loses the
 * category's own heading along with the chart that wanted the data, and the
 * console names the first line that touched the gap rather than the cause.
 *
 * That has happened twice on the list profile, for `covered` and for
 * `region.spread`, which is why this response is guarded before it is returned
 * rather than after somebody is bitten.
 *
 * Nested objects are given a value for the same reason as the lists: reading
 * `reach.website` off a missing `reach` throws exactly as iterating a missing
 * array does. Bare scalars are left alone — a number that is absent reads as
 * zero and shows a wrong figure, which is a different problem with a different
 * fix.
 */
export async function fetchIndustryDetails(id: number): Promise<IndustryDetails> {
  const response = await apiClient.get<IndustryDetails>(
    `/deep-iq/industry-centroids/${encodeURIComponent(String(id))}`
  );
  return withEveryList(response.data);
}

function withEveryList(details: IndustryDetails): IndustryDetails {
  return {
    ...details,
    sizes: { ...details.sizes, bands: details.sizes?.bands ?? [] },
    founding: details.founding ?? [],
    founding_months: details.founding_months ?? [],
    legal_forms: details.legal_forms ?? [],
    cantons: details.cantons ?? [],
    cities: details.cities ?? [],
    reach: details.reach ?? noReach,
    capital: details.capital ?? noCapital,
    signals: {
      days_30: details.signals?.days_30 ?? noSignals,
      days_90: details.signals?.days_90 ?? noSignals,
      days_365: details.signals?.days_365 ?? noSignals
    }
  };
}
